/**
 * Transport-level API contract.
 *
 * The platform envelope shape is one of the assumptions that must be confirmed
 * against `tame-platform-backend`. It is isolated here and in
 * `apps/web/src/lib/api/client.ts` so reconciling it is a two-file change.
 */

/** ISO-8601 instant in UTC. Every timestamp on the wire uses this form. */
export type IsoDateTime = string;

/** ISO-8601 calendar date, `YYYY-MM-DD`, with no timezone component. */
export type IsoDate = string;

/** Local wall-clock time, `HH:mm`, interpreted in the business timezone. */
export type LocalTime = string;

export type Uuid = string;

/** Successful platform response envelope. */
export interface ApiSuccess<T> {
  success: true;
  data: T;
  meta?: ApiMeta;
}

/** Failure envelope. `code` is machine-readable; `message` is user-safe. */
export interface ApiFailure {
  success: false;
  error: {
    code: ApiErrorCode;
    message: string;
    /** Field-level validation errors keyed by dotted field path. */
    fieldErrors?: Record<string, string[]>;
    /** Present on 409 conflict responses from booking acceptance. */
    details?: unknown;
  };
}

export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

export interface ApiMeta {
  pagination?: PaginationMeta;
  /** Server time, used to detect significant client clock skew. */
  serverTime?: IsoDateTime;
}

export const API_ERROR_CODES = [
  'VALIDATION_FAILED',
  'UNAUTHENTICATED',
  'EMAIL_NOT_VERIFIED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'SCHEDULING_CONFLICT',
  'CAPACITY_EXCEEDED',
  'INSUFFICIENT_CREDITS',
  'ONBOARDING_INCOMPLETE',
  'CONSENT_REQUIRED',
  'HEALTH_CLEARANCE_REQUIRED',
  'CLIENT_SUSPENDED',
  'INVALID_STATE_TRANSITION',
  'IMMUTABLE_RESOURCE',
  'RATE_LIMITED',
  'PAYLOAD_TOO_LARGE',
  'UNSUPPORTED_MEDIA_TYPE',
  'FEATURE_DISABLED',
  'SERVER_ERROR',
  'NETWORK_ERROR',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/** User-facing copy for each error code. Never exposes internal detail. */
export const API_ERROR_MESSAGES: Record<ApiErrorCode, string> = {
  VALIDATION_FAILED: 'Please check the highlighted fields and try again.',
  UNAUTHENTICATED: 'Please sign in to continue.',
  EMAIL_NOT_VERIFIED: 'Please verify your email address to continue.',
  FORBIDDEN: 'You do not have permission to do that.',
  NOT_FOUND: 'We could not find what you were looking for.',
  CONFLICT: 'That change conflicts with the current state. Please reload and try again.',
  SCHEDULING_CONFLICT: 'That time is no longer available. Please choose another slot.',
  CAPACITY_EXCEEDED: 'That session is now full.',
  INSUFFICIENT_CREDITS: 'You do not have enough session credits for this booking.',
  ONBOARDING_INCOMPLETE: 'Please complete your onboarding before booking a training session.',
  CONSENT_REQUIRED: 'Please review and accept the required policies to continue.',
  HEALTH_CLEARANCE_REQUIRED:
    'Your coach needs to review your health screening before this booking can be confirmed.',
  CLIENT_SUSPENDED: 'This account is currently suspended. Please contact your coach.',
  INVALID_STATE_TRANSITION: 'That action is not available for the current status.',
  IMMUTABLE_RESOURCE: 'Published versions cannot be edited. Create a new version instead.',
  RATE_LIMITED: 'Too many attempts. Please wait a moment and try again.',
  PAYLOAD_TOO_LARGE: 'That file is too large.',
  UNSUPPORTED_MEDIA_TYPE: 'That file type is not supported.',
  FEATURE_DISABLED: 'That feature is not currently enabled.',
  SERVER_ERROR: 'Something went wrong on our end. Please try again shortly.',
  NETWORK_ERROR: 'We could not reach the server. Please check your connection.',
};

// --- Pagination, sorting, filtering ------------------------------------------

export interface PaginationMeta {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface Paginated<T> {
  items: T[];
  pagination: PaginationMeta;
}

export type SortDirection = 'asc' | 'desc';

export interface ListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortDirection?: SortDirection;
}

export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;

export function emptyPage<T>(pageSize = DEFAULT_PAGE_SIZE): Paginated<T> {
  return {
    items: [],
    pagination: {
      page: 1,
      pageSize,
      totalItems: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPreviousPage: false,
    },
  };
}

// --- Two-phase private uploads ----------------------------------------------

export interface UploadTicketRequest {
  fileName: string;
  contentType: string;
  /** Bytes. Validated server-side against the category limit. */
  contentLength: number;
}

export interface UploadTicket {
  /** Server-generated, non-guessable object key. Browser names are never used. */
  objectKey: string;
  /** Presigned PUT URL. Short-lived. */
  uploadUrl: string;
  /** Headers that must be sent verbatim with the PUT. */
  requiredHeaders: Record<string, string>;
  expiresAt: IsoDateTime;
  maxContentLength: number;
}

export interface SignedDownload {
  url: string;
  expiresAt: IsoDateTime;
}

/** Content types accepted for progress photographs. */
export const PROGRESS_PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/** Content types accepted for client documents. Deliberately narrow. */
export const DOCUMENT_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

export const MAX_PROGRESS_PHOTO_BYTES = 12 * 1024 * 1024;
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;
export const MAX_MESSAGE_ATTACHMENT_BYTES = 10 * 1024 * 1024;
